import React, { useState, useEffect, useRef } from "react";
import AuthScreen from "./components/AuthScreen";
import Header from "./components/Header";
import Dashboard from "./components/Dashboard";
import OrganOverview from "./components/OrganOverview";
import OrganModule from "./components/OrganModule";
import AvatarCustomizer from "./components/AvatarCustomizer";
import Leaderboard from "./components/Leaderboard";
import Games from "./components/Games";
import SymptomDetective from "./components/SymptomDetective";
import PubQuiz from "./components/PubQuiz";
import MedicalCrossword from "./components/MedicalCrossword";
import MedicalMatch from "./components/MedicalMatch";
import LabInterpreter from "./components/LabInterpreter";
import DiagnosePatient from "./components/DiagnosePatient";
import MedRPGOverworld from "./components/MedRPGOverworld";
import LiveSessionPresenter from "./components/LiveSessionPresenter";
import DailyBonusModal from "./components/DailyBonusModal";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { loadUserData as loadUserDataFromDB, saveUserData as saveUserDataToDB, saveStreakData, saveAvatarStats } from "./lib/database";
import { initGA, trackPageView, trackLevelUp, trackEvent } from "./lib/analytics";
import { getStreakBonus } from "./lib/streakUtils";
import { applyBoost, STUDY_BOOST } from "./lib/tamagotchiUtils";

const MedQuestApp = () => {
  const { user, signIn, signUp, signOut } = useAuth();
  const getInitialView = () => {
    if (typeof window === "undefined") return "login";
    return localStorage.getItem("medquestLastView") || "login";
  };
  const [view, setView] = useState(getInitialView);
  const [userData, setUserData] = useState(null);
  const userDataRef = useRef(null);
  useEffect(() => { userDataRef.current = userData; }, [userData]);
  const [showDailyBonus, setShowDailyBonus] = useState(false);
  const [dailyBonusData, setDailyBonusData] = useState(null);

  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
  }, []);

  // Track page views when view changes
  useEffect(() => {
    trackPageView(view);
    if (typeof window !== "undefined") {
      localStorage.setItem("medquestLastView", view);
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [view]);

  useEffect(() => {
    if (user) {
      if (view === "login") {
        const savedView =
          typeof window !== "undefined" ? localStorage.getItem("medquestLastView") : null;
        setView(savedView && savedView !== "login" ? savedView : "dashboard");
      }
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUserData = async () => {
    try {
      const data = await loadUserDataFromDB(user.id);
      setUserData(data);

      // Save back decayed avatar stats (fire-and-forget, non-blocking)
      if (data.avatarStats) {
        saveAvatarStats(user.id, data.avatarStats).catch(console.error);
      }

      // Check if we should show daily bonus
      if (data._streakResult?.isNewDay && !data.streakData?.dailyBonusCollectedToday) {
        setDailyBonusData({
          streak: data.streakData.currentStreak,
          bonusCoins: getStreakBonus(data.streakData.currentStreak),
          streakBroken: data._streakResult.streakBroken,
        });
        setShowDailyBonus(true);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const collectDailyBonus = async () => {
    if (!dailyBonusData) return;

    const bonusCoins = dailyBonusData.bonusCoins;
    const newStreakData = {
      ...userData.streakData,
      dailyBonusCollectedToday: true,
      totalStreakCoinsEarned: (userData.streakData?.totalStreakCoinsEarned || 0) + bonusCoins,
    };

    const newUserData = {
      ...userData,
      coins: userData.coins + bonusCoins,
      streakData: newStreakData,
    };

    await saveUserDataToDB(user.id, newUserData);
    await saveStreakData(user.id, newStreakData);
    setUserData(newUserData);

    // Track analytics
    trackEvent('daily_bonus_collected', {
      streak_day: dailyBonusData.streak,
      coins_earned: bonusCoins,
    });

    setShowDailyBonus(false);
    setDailyBonusData(null);
  };

  const saveUserData = async (data) => {
    try {
      await saveUserDataToDB(user.id, data);
      setUserData(data);
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  };

  const handleLogin = async (email, password) => {
    await signIn(email, password);
    setView("dashboard");
  };

  const handleSignup = async (email, password) => {
    await signUp(email, password);
    setView("dashboard");
  };

  const handleLogout = async () => {
    await signOut();
    setView("login");
    if (typeof window !== "undefined") {
      localStorage.setItem("medquestLastView", "login");
    }
  };

  const addXP = async (amount, isReview) => {
    const latestData = userDataRef.current || userData;
    const actualXP = isReview ? 0 : amount;
    const reviewCoins = isReview ? Math.floor(amount / 50) : 0;
    const newXP = latestData.xp + actualXP;
    const newWeeklyXP = (latestData.weeklyXP || 0) + actualXP;
    const newCoins =
      latestData.coins + (isReview ? reviewCoins : Math.floor(amount / 10));
    const oldLevel = latestData.level;
    let newLevel = oldLevel;

    if (newXP >= 5000) newLevel = "Specialist";
    else if (newXP >= 2000) newLevel = "Resident";
    else if (newXP >= 500) newLevel = "Junior Doctor";
    else newLevel = "Med Student";

    // Track level up if level changed
    if (newLevel !== oldLevel) {
      trackLevelUp(oldLevel, newLevel, newXP);
    }

    // Boost avatar stats when completing a module (not on review)
    const newAvatarStats = isReview
      ? latestData.avatarStats
      : applyBoost(latestData.avatarStats, STUDY_BOOST);

    await saveUserData({
      ...latestData,
      xp: newXP,
      weeklyXP: newWeeklyXP,
      coins: newCoins,
      level: newLevel,
      avatarStats: newAvatarStats,
    });
  };

  if (!user) {
    return <AuthScreen onLogin={handleLogin} onSignup={handleSignup} />;
  }

  if (!userData)
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-300 via-gray-900 to-slate-800">
      {showDailyBonus && dailyBonusData && (
        <DailyBonusModal
          streakData={dailyBonusData}
          onCollect={collectDailyBonus}
          onClose={() => setShowDailyBonus(false)}
        />
      )}
      <Header
        userData={userData}
        onViewChange={setView}
        onLogout={handleLogout}
      />

      {view === "dashboard" && (
        <Dashboard userData={userData} onViewChange={setView} userId={user.id} />
      )}
      {view === "heart-overview" && (
        <OrganOverview
          organ="heart"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "brain-overview" && (
        <OrganOverview
          organ="brain"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "lungs-overview" && (
        <OrganOverview
          organ="lungs"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "colon-overview" && (
        <OrganOverview
          organ="colon"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "liver-overview" && (
        <OrganOverview
          organ="liver"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "kidney-overview" && (
        <OrganOverview
          organ="kidney"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "womenreproductive-overview" && (
        <OrganOverview
          organ="womenreproductive"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "skeleton-overview" && (
        <OrganOverview
          organ="skeleton"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "thyroid-overview" && (
        <OrganOverview
          organ="thyroid"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "adrenalgland-overview" && (
        <OrganOverview
          organ="adrenalgland"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "genetics-overview" && (
        <OrganOverview
          organ="genetics"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "genetics-methods-overview" && (
        <OrganOverview
          organ="genetics-methods"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("genetics-overview")}
          saveUserData={saveUserData}
        />
      )}
      {view === "usmle1-overview" && (
        <OrganOverview
          organ="usmle1"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {view === "usmle2-overview" && (
        <OrganOverview
          organ="usmle2"
          userData={userData}
          onViewChange={setView}
          onBack={() => setView("dashboard")}
          saveUserData={saveUserData}
        />
      )}
      {(view === "heart" || view.startsWith("heart-module-")) && (
        <OrganModule
          organ="heart"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("heart-overview")}
          initialModuleIndex={view.startsWith("heart-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "brain" || view.startsWith("brain-module-")) && (
        <OrganModule
          organ="brain"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("brain-overview")}
          initialModuleIndex={view.startsWith("brain-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "lungs" || view.startsWith("lungs-module-")) && (
        <OrganModule
          organ="lungs"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("lungs-overview")}
          initialModuleIndex={view.startsWith("lungs-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "colon" || view.startsWith("colon-module-")) && (
        <OrganModule
          organ="colon"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("colon-overview")}
          initialModuleIndex={view.startsWith("colon-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "liver" || view.startsWith("liver-module-")) && (
        <OrganModule
          organ="liver"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("liver-overview")}
          initialModuleIndex={view.startsWith("liver-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "kidney" || view.startsWith("kidney-module-")) && (
        <OrganModule
          organ="kidney"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("kidney-overview")}
          initialModuleIndex={view.startsWith("kidney-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "womenreproductive" || view.startsWith("womenreproductive-module-")) && (
        <OrganModule
          organ="womenreproductive"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("womenreproductive-overview")}
          initialModuleIndex={view.startsWith("womenreproductive-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "thyroid" || view.startsWith("thyroid-module-")) && (
        <OrganModule
          organ="thyroid"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("thyroid-overview")}
          initialModuleIndex={view.startsWith("thyroid-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "skeleton" || view.startsWith("skeleton-module-")) && (
        <OrganModule
          organ="skeleton"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("skeleton-overview")}
          initialModuleIndex={view.startsWith("skeleton-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "adrenalgland" || view.startsWith("adrenalgland-module-")) && (
        <OrganModule
          organ="adrenalgland"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("adrenalgland-overview")}
          initialModuleIndex={view.startsWith("adrenalgland-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "genetics" || view.startsWith("genetics-module-")) && (
        <OrganModule
          organ="genetics"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("genetics-overview")}
          initialModuleIndex={view.startsWith("genetics-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "genetics-methods" || view.startsWith("genetics-methods-module-")) && (
        <OrganModule
          organ="genetics-methods"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("genetics-methods-overview")}
          initialModuleIndex={view.startsWith("genetics-methods-module-") ? parseInt(view.split("-")[3]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "usmle1" || view.startsWith("usmle1-module-")) && (
        <OrganModule
          organ="usmle1"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("usmle1-overview")}
          initialModuleIndex={view.startsWith("usmle1-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {(view === "usmle2" || view.startsWith("usmle2-module-")) && (
        <OrganModule
          organ="usmle2"
          userData={userData}
          saveUserData={saveUserData}
          onComplete={addXP}
          onBack={() => setView("usmle2-overview")}
          initialModuleIndex={view.startsWith("usmle2-module-") ? parseInt(view.split("-")[2]) : 0}
          forceRestart={view.endsWith("-reset")}
        />
      )}
      {view === "avatar" && (
        <AvatarCustomizer
          userData={userData}
          onSave={saveUserData}
          onBack={() => setView("dashboard")}
        />
      )}
      {view === "games" && (
        <Games onViewChange={setView} onBack={() => setView("dashboard")} />
      )}
      {view === "guess-disease" && (
        <SymptomDetective
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "pubquiz" && (
        <PubQuiz
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "crossword" && (
        <MedicalCrossword
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "match" && (
        <MedicalMatch
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "lab-interpreter" && (
        <LabInterpreter
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "diagnose-patient" && (
        <DiagnosePatient
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "med-rpg" && (
        <MedRPGOverworld
          onBack={() => setView("games")}
          userData={userData}
          saveUserData={saveUserData}
        />
      )}
      {view === "live-session" && (
        <LiveSessionPresenter
          onBack={() => setView("games")}
          userData={userData}
        />
      )}
      {view === "leaderboard" && (
        <Leaderboard
          onBack={() => setView("dashboard")}
          userData={userData}
          userId={user.id}
          onUserDataChange={setUserData}
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <MedQuestApp />
    </AuthProvider>
  );
};

export default App;
